/**
 * The seeded painted nebula (spec §8; style guide §1.1, §3.3): domain-warped layered noise in three saturated hues,
 * fractal dust lanes, a galactic band and a brightest lobe on the rim light direction. Evaluated only by the bake
 * (sky/bake.ts), never per frame. Output: rgb = linear radiance (clamped for the 8-bit sRGB target),
 * a = dust extinction 0..1 (the live star layer reads it to dim stars behind dust lanes).
 * Tunable values are uniforms, so a tuning change re-bakes without recompiling. The seed-dependent values (noise
 * offsets, second lobe, band normal) are uniforms too (M3 R3): `reseed` re-seeds the same compiled pipeline for
 * another star system, so a system switch costs a bake, never a shader compile.
 */
import { Color, Vector3, type Node, type UniformNode } from 'three/webgpu'
import {
  abs,
  dot,
  exp,
  float,
  hash,
  max,
  min,
  mix,
  normalize,
  positionLocal,
  pow,
  saturate,
  smoothstep,
  sRGBTransferEOTF,
  sRGBTransferOETF,
  uniform,
  vec3,
  vec4,
} from 'three/tsl'
import { createRng, range } from '../../../core/rng'
import { unitDirection, type EnvironmentFile, type Vec3Tuple } from '../data'
import { fbmVec3 } from './noise'

export type NebulaConfig = EnvironmentFile['sky']['nebula']

const DEG = Math.PI / 180

export interface NebulaUniforms {
  readonly hueA: UniformNode<'color', Color>
  readonly hueB: UniformNode<'color', Color>
  readonly hueC: UniformNode<'color', Color>
  readonly base: UniformNode<'color', Color>
  readonly brightness: UniformNode<'float', number>
  readonly warp: UniformNode<'float', number>
  readonly dust: UniformNode<'float', number>
  readonly bandGlow: UniformNode<'float', number>
  readonly secondLobe: UniformNode<'float', number>
  readonly maxRadiance: UniformNode<'float', number>
  /** Edge softness in units of a 1024² face texel (4 on a 256² face) so hard edges never alias. */
  readonly soft: UniformNode<'float', number>
  /** Seeded (M3 R3, `reseed`): the two noise-domain offsets, the main and second lobe and the band normal. */
  readonly offsetA: UniformNode<'vec3', Vector3>
  readonly offsetB: UniformNode<'vec3', Vector3>
  readonly lobe: UniformNode<'vec3', Vector3>
  readonly lobe2: UniformNode<'vec3', Vector3>
  readonly band: UniformNode<'vec3', Vector3>
}

export interface NebulaShader {
  /** vec4(linear rgb, extinction), a function of the bake mesh's local direction. */
  readonly node: Node<'vec4'>
  readonly uniforms: NebulaUniforms
  /** Pushes new config values into the uniforms (the caller re-bakes). */
  set(cfg: NebulaConfig): void
  /** Sets the edge softness for the face size about to be baked. */
  setFaceSize(size: number): void
  /** Re-seeds the noise offsets, the second lobe and the band for another system (the caller re-bakes). */
  reseed(seed: number, lobeDir: Vec3Tuple, bandNormal: Vec3Tuple): void
}

/** The two seeded noise-domain offsets (each component in [-80, 80]). Deterministic for a seed. */
export function nebulaOffsets(seed: number): [[number, number, number], [number, number, number]] {
  const rng = createRng(seed >>> 0)
  const off = (): [number, number, number] => [range(rng, -80, 80), range(rng, -80, 80), range(rng, -80, 80)]
  const o1 = off()
  const o2 = off()
  return [o1, o2]
}

/** Lobe sharpness k for exp(k (cos t - 1)) so the lobe falls to 1/e at `widthDeg` from its centre. */
export function lobeSharpness(widthDeg: number): number {
  return 1 / (1 - Math.cos(widthDeg * DEG))
}

/** A seeded secondary lobe direction, 70-130 degrees from the main lobe, so the sky never has one lonely blob. */
export function secondLobeDirection(seed: number, lobe: Vec3Tuple): [number, number, number] {
  const rng = createRng((seed ^ 0x68e31da4) >>> 0)
  const a = unitDirection(lobe)
  const ref: Vec3Tuple = Math.abs(a[1]) < 0.9 ? [0, 1, 0] : [1, 0, 0]
  const t = unitDirection([ref[1] * a[2] - ref[2] * a[1], ref[2] * a[0] - ref[0] * a[2], ref[0] * a[1] - ref[1] * a[0]])
  const b: [number, number, number] = [a[1] * t[2] - a[2] * t[1], a[2] * t[0] - a[0] * t[2], a[0] * t[1] - a[1] * t[0]]
  const phi = range(rng, 0, Math.PI * 2)
  const theta = range(rng, 70, 130) * DEG
  const px = t[0] * Math.cos(phi) + b[0] * Math.sin(phi)
  const py = t[1] * Math.cos(phi) + b[1] * Math.sin(phi)
  const pz = t[2] * Math.cos(phi) + b[2] * Math.sin(phi)
  return unitDirection([
    a[0] * Math.cos(theta) + px * Math.sin(theta),
    a[1] * Math.cos(theta) + py * Math.sin(theta),
    a[2] * Math.cos(theta) + pz * Math.sin(theta),
  ])
}

/** Edge softness for a face size: 1 at 1024², 4 at 256² (texel angle relative to a 1024² face). */
export function faceSoftness(size: number): number {
  return Math.max(1, 1024 / size)
}

export function createNebulaShader(cfg: NebulaConfig, seed: number, lobeDir: Vec3Tuple, bandNormal: Vec3Tuple): NebulaShader {
  const [off1, off2] = nebulaOffsets(seed)
  const o1 = uniform(new Vector3(off1[0], off1[1], off1[2]))
  const o2 = uniform(new Vector3(off2[0], off2[1], off2[2]))
  const lobe = unitDirection(lobeDir)
  const lobe2v = secondLobeDirection(seed, lobe)
  const lobe2 = uniform(new Vector3(lobe2v[0], lobe2v[1], lobe2v[2]))
  const bandv = unitDirection(bandNormal)
  const band = uniform(new Vector3(bandv[0], bandv[1], bandv[2]))

  const u: NebulaUniforms = {
    hueA: uniform(new Color(cfg.hues[0])),
    hueB: uniform(new Color(cfg.hues[1])),
    hueC: uniform(new Color(cfg.hues[2])),
    base: uniform(new Color(cfg.base)),
    brightness: uniform(cfg.brightness),
    warp: uniform(cfg.warp),
    dust: uniform(cfg.dust),
    bandGlow: uniform(cfg.bandGlow),
    secondLobe: uniform(cfg.secondLobe),
    maxRadiance: uniform(cfg.maxRadiance),
    soft: uniform(1),
    offsetA: o1,
    offsetB: o2,
    lobe: uniform(new Vector3(lobe[0], lobe[1], lobe[2])),
    lobe2,
    band,
  }
  const uLobeDir = u.lobe

  const p = normalize(positionLocal)
  const s = cfg.scale
  // Large-scale envelope: the main lobe (rim light side), a seeded second lobe and the galactic band.
  const kMain = lobeSharpness(cfg.lobeWidthDeg)
  const kSecond = lobeSharpness(cfg.lobeWidthDeg * 0.8)
  const lobeMain = exp(dot(p, uLobeDir).sub(1).mul(kMain))
  const lobeSecond = exp(dot(p, lobe2).sub(1).mul(kSecond)).mul(u.secondLobe)
  const bandW = Math.sin(cfg.bandWidthDeg * DEG)
  const bandLat = dot(p, band)
  const bandMask = exp(bandLat.mul(bandLat).div(-(bandW * bandW)))
  const envelope = saturate(lobeMain.add(lobeSecond).add(bandMask.mul(u.bandGlow)))

  // Only two noise calls (each yields three decorrelated channels): cold pipeline compile is what bounds the
  // bake time (sky/noise.ts). q: low-frequency warp field, broad gas and hue regions. d: detail, wisps and dust.
  const q = fbmVec3(p.mul(s).add(o1), 4)
  const p2 = p.mul(s).add(q.mul(u.warp))
  const d = fbmVec3(p2.mul(cfg.detail).add(o2).add(q.zxy.mul(u.warp.mul(0.35))), 6)

  // Gas density: broad low-frequency structure inside the envelope, textured by the detail octaves.
  const broad = saturate(q.x.mul(0.45).add(0.5))
  const fine = saturate(d.x.mul(0.5).add(0.5))
  const shaped = saturate(broad.mul(0.9).add(envelope.mul(0.8)).sub(0.8).mul(1.8))
  const density = pow(shaped.mul(mix(float(0.35), float(1.3), fine)), 1.35)
  // Wisps: ridged filaments at the thin edges of the gas.
  const ridge = pow(saturate(float(1).sub(abs(d.y))), 9)
  const wisps = ridge.mul(smoothstep(0.01, 0.2, density)).mul(float(1).sub(smoothstep(0.35, 0.8, density))).mul(envelope)

  // Colour: regions drift between violet and teal; dense gas near the main lobe warms to the accent hue.
  const hueSel = smoothstep(-0.3, 0.35, q.z)
  const thinCol = mix(u.hueA, u.hueB, hueSel)
  const denseCol = mix(mix(u.hueA, u.hueB, hueSel.mul(0.6)), u.hueC, smoothstep(0.55, 0.97, lobeMain).mul(0.85))
  const gasCol = mix(thinCol, denseCol, smoothstep(0.2, 0.8, density))
  const core = pow(density, 2.5).mul(pow(lobeMain, 6))
  const hot = mix(u.hueC, vec3(1.0, 0.85, 0.75), smoothstep(0.4, 1.0, core).mul(0.8)).mul(core)
  // Faint unresolved-star glow along the band: white-noise grain (a hash, no noise call).
  const grain = hash(p.x.mul(311.7).add(p.y.mul(183.3)).add(p.z.mul(271.9)).mul(1013.0))
  const bandHaze = vec3(0.55, 0.6, 0.8).mul(bandMask.mul(u.bandGlow).mul(0.035).mul(grain.mul(0.6).add(0.4)))
  // Faint gas everywhere so the envelope never ends in a wall.
  const ambientGas = pow(saturate(broad.sub(0.35).mul(1.6)), 2).mul(fine).mul(0.07)
  const emission = thinCol.mul(ambientGas).add(gasCol.mul(density)).add(thinCol.mul(wisps.mul(0.5))).add(hot).mul(u.brightness).add(bandHaze)

  // Dust: fractal "cauliflower" clouds, only where there is gas to block.
  const dustEdge = u.soft.sqrt().mul(0.08)
  const dustiness = smoothstep(float(0.12).sub(dustEdge), float(0.12).add(dustEdge), d.z.add(d.x.mul(0.3)))
  const ext = saturate(dustiness.mul(u.dust).mul(smoothstep(0.02, 0.35, density.add(envelope.mul(0.1)))))
  const dustLit = mix(u.hueC, u.hueA, 0.5).mul(ext.mul(density).mul(0.03).mul(u.brightness))
  // Dust reddens what it does not block: blue is extinguished faster than red.
  const tau = ext.mul(3.4)
  const transmit = exp(vec3(tau.mul(-0.7), tau.mul(-1.0), tau.mul(-1.3)))
  const lit = emission.mul(transmit).add(dustLit).add(u.base)

  // Clamp for the 8-bit sRGB target, then dither by half a code value in sRGB space (no banding in the darks).
  const clamped = min(max(lit, vec3(0, 0, 0)), vec3(u.maxRadiance, u.maxRadiance, u.maxRadiance))
  const dither = hash(p.x.mul(12.9898).add(p.y.mul(78.233)).add(p.z.mul(37.719)).mul(43758.5453)).sub(0.5).div(255)
  const encoded = sRGBTransferOETF(clamped) as Node<'vec3'>
  const out = sRGBTransferEOTF(saturate(encoded.add(dither))) as Node<'vec3'>

  return {
    node: vec4(out, ext),
    uniforms: u,
    setFaceSize(size: number): void {
      u.soft.value = faceSoftness(size)
    },
    reseed(nextSeed: number, nextLobeDir: Vec3Tuple, nextBandNormal: Vec3Tuple): void {
      const [a, b] = nebulaOffsets(nextSeed)
      o1.value.set(a[0], a[1], a[2])
      o2.value.set(b[0], b[1], b[2])
      const l = unitDirection(nextLobeDir)
      uLobeDir.value.set(l[0], l[1], l[2])
      const l2 = secondLobeDirection(nextSeed, l)
      lobe2.value.set(l2[0], l2[1], l2[2])
      const n = unitDirection(nextBandNormal)
      band.value.set(n[0], n[1], n[2])
    },
    set(c: NebulaConfig): void {
      u.hueA.value.set(c.hues[0])
      u.hueB.value.set(c.hues[1])
      u.hueC.value.set(c.hues[2])
      u.base.value.set(c.base)
      u.brightness.value = c.brightness
      u.warp.value = c.warp
      u.dust.value = c.dust
      u.bandGlow.value = c.bandGlow
      u.secondLobe.value = c.secondLobe
      u.maxRadiance.value = c.maxRadiance
    },
  }
}
